import { action, makeObservable, runInAction } from 'mobx'
import {
  mapCallCenterContact,
  CallCenterContact,
} from 'call-center/models/CallCenterContact'
import {
  CallCenterSearchResult,
  CallCenterWorkspaceMemberMatch,
} from 'call-center/models/CallCenterSearchResult'
import {
  filterCallCenterMemberships,
  filterCallCenterWorkspaceMembersMatches,
} from 'call-center/utils'
import {
  CallCenterResultMerger,
  dedupeCallCenterMatches,
  sortMatchesByLatestLogin,
} from 'call-center/services/CallCenterResultMerger'
import { FreshbooksCallCenterAPI } from 'freshbooks/API'
import { IAMAPI } from 'iam/API'
import MemberStore from 'iam/MemberStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import { UserDataType } from 'iam/models/UserDataType'
import { CursorPaged, Id } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'

interface ContactResults {
  contacts: CallCenterContact[]
  hasMore: boolean
}

interface MemberResults {
  matches: CallCenterWorkspaceMemberMatch[]
  hasMore: boolean
}

export class CallCenterSearchService {
  private contactCursor: string | null | undefined = undefined
  private workspaceIds: string[] = []
  private workspaceCursors = new Map<string, string | null | undefined>()
  private aggregateCursorIndex = 0
  private resultMerger = new CallCenterResultMerger()

  constructor(
    private freshbooksCallCenterAPI: FreshbooksCallCenterAPI,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private iamAPI: IAMAPI,
    private memberStore: MemberStore,
  ) {
    makeObservable(this)
  }

  @action
  prepareWorkspaceContext() {
    const memberships = filterCallCenterMemberships(
      this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships(),
    )

    this.workspaceIds = memberships.map((membership) =>
      Id.assert(membership.workspace.id),
    )

    this.workspaceIds.forEach((workspaceId) => {
      this.workspaceCursors.set(workspaceId, undefined)
    })
  }

  async search(
    query: string,
    limit: number,
    cancellationToken: CancellationToken,
  ): Promise<CursorPaged<CallCenterSearchResult>> {
    if (!query) {
      return CursorPaged.empty
    }

    const [contactsPage, workspacePage] = await Promise.all([
      this.fetchContactResults(query, limit, cancellationToken),
      this.fetchWorkspaceMatches(query, limit, cancellationToken),
    ])

    cancellationToken.throwIfCancelled()

    const newResults = this.mergeSearchResults(
      contactsPage.contacts,
      workspacePage.matches,
    )

    const hasMore = contactsPage.hasMore || workspacePage.hasMore

    return {
      data: newResults,
      cursor: hasMore ? this.nextAggregateCursor() : null,
    }
  }

  private async fetchContactResults(
    query: string,
    limit: number,
    cancellationToken: CancellationToken,
  ): Promise<ContactResults> {
    if (this.contactCursor === null) {
      return { contacts: [], hasMore: false }
    }

    const response = await this.freshbooksCallCenterAPI.searchContacts(
      {
        search: query,
        limit,
        after: this.contactCursor ?? undefined,
      },
      cancellationToken,
    )

    cancellationToken.throwIfCancelled()

    this.contactCursor = response.cursor || null

    return {
      contacts: response.data.map(mapCallCenterContact),
      hasMore: this.contactCursor !== null,
    }
  }

  private async fetchWorkspaceMatches(
    search: string,
    limit: number,
    cancellationToken: CancellationToken,
  ): Promise<MemberResults> {
    if (this.workspaceIds.length === 0) {
      return { matches: [], hasMore: false }
    }

    const workspaceIdsToFetch = this.workspaceIds.filter(
      (workspaceId) => this.workspaceCursors.get(workspaceId) !== null,
    )

    if (workspaceIdsToFetch.length === 0) {
      return { matches: [], hasMore: false }
    }

    const responses = await Promise.all(
      workspaceIdsToFetch.map(async (workspaceId) => {
        const after = this.workspaceCursors.get(workspaceId) ?? undefined
        try {
          const response = await this.iamAPI.members.find(
            Id.assert(workspaceId),
            {
              search_text: search,
              limit,
              workspace_id: Id.assert(workspaceId),
              user_types: [UserDataType.User],
              after,
            },
            cancellationToken,
          )
          return { workspaceId, response }
        } catch (err) {
          return { workspaceId, response: null }
        }
      }),
    )

    cancellationToken.throwIfCancelled()

    const matches: CallCenterWorkspaceMemberMatch[] = []

    runInAction(() => {
      responses.forEach(({ workspaceId, response }) => {
        if (!response) {
          this.workspaceCursors.set(workspaceId, null)
          return
        }

        response.data.forEach((dto) => {
          const member = this.memberStore.primeFullMember(dto)
          matches.push({ member, workspace: member.workspace })
        })

        this.workspaceCursors.set(workspaceId, response.cursor || null)
      })
    })

    return {
      matches: sortMatchesByLatestLogin(
        filterCallCenterWorkspaceMembersMatches(
          dedupeCallCenterMatches(matches),
        ),
      ),
      hasMore: this.workspaceIds.some(
        (workspaceId) => this.workspaceCursors.get(workspaceId) !== null,
      ),
    }
  }

  private mergeSearchResults(
    hubspotContacts: CallCenterContact[],
    workspaceMembersMatches: CallCenterWorkspaceMemberMatch[],
  ): CallCenterSearchResult[] {
    const newlyAdded: CallCenterSearchResult[] = []

    runInAction(() => {
      newlyAdded.push(...this.resultMerger.mergeContacts(hubspotContacts))
      newlyAdded.push(
        ...this.resultMerger.mergeWorkspaceMembers(workspaceMembersMatches),
      )
    })

    return newlyAdded
  }

  async searchWorkspaceMembers(
    search: string,
    limit: number,
  ): Promise<CallCenterWorkspaceMemberMatch[]> {
    const memberships = filterCallCenterMemberships(
      this.workspaceMembershipStore.getCurrentUserWorkspaceMemberships(),
    )

    const workspaceIds = memberships.map(
      (membership) => membership.workspace.id,
    )

    if (workspaceIds.length === 0) {
      return []
    }

    const responses = await Promise.all(
      workspaceIds.map((workspaceId) =>
        this.iamAPI.members
          .find(Id.assert(workspaceId), {
            search_text: search,
            limit,
            workspace_id: Id.assert(workspaceId),
            user_types: [UserDataType.User],
          })
          .catch(() => null),
      ),
    )

    const matches: CallCenterWorkspaceMemberMatch[] = []
    responses.forEach((response) => {
      if (!response) {
        return
      }
      response.data.forEach((dto) => {
        const member = this.memberStore.primeFullMember(dto)
        matches.push({ member, workspace: member.workspace })
      })
    })

    return sortMatchesByLatestLogin(
      filterCallCenterWorkspaceMembersMatches(dedupeCallCenterMatches(matches)),
    )
  }

  getResultMerger() {
    return this.resultMerger
  }

  private nextAggregateCursor(): string {
    this.aggregateCursorIndex += 1
    return `${this.aggregateCursorIndex}`
  }

  @action
  reset() {
    this.contactCursor = undefined
    this.workspaceIds = []
    this.workspaceCursors.clear()
    this.aggregateCursorIndex = 0
    this.resultMerger.clear()
  }
}
